import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, RefreshCw, X, ArrowUpCircle } from 'lucide-react';
import currentVersionInfo from '../version.json';

interface VersionInfo {
    version: string;
    packageVersion?: string;
    buildNumber?: number;
    buildTime?: number;
    formattedDate?: string;
}

export const UpdateBanner: React.FC = () => {
    const [hasUpdate, setHasUpdate] = useState<boolean>(false);
    const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [isDismissed, setIsDismissed] = useState<boolean>(false);

    const checkForUpdate = useCallback(async () => {
        try {
            // Append cache-busting timestamp parameter to bypass browser/CDN HTTP caching
            const res = await fetch(`/version.json?t=${Date.now()}`, {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });

            if (!res.ok) return;

            const serverVersion: VersionInfo = await res.json();

            // Compare build numbers or build times
            const serverBuildTime = serverVersion.buildTime || 0;
            const clientBuildTime = (currentVersionInfo as VersionInfo).buildTime || 0;
            const serverBuildNum = serverVersion.buildNumber || 0;
            const clientBuildNum = (currentVersionInfo as VersionInfo).buildNumber || 0;

            const isNewer = serverBuildTime > clientBuildTime ||
                serverBuildNum > clientBuildNum ||
                serverVersion.version !== (currentVersionInfo as VersionInfo).version;

            if (isNewer) {
                setLatestVersion(serverVersion);
                setHasUpdate(true);
            }
        } catch (err) {
            // Quietly ignore network check failures
        }
    }, []);

    useEffect(() => {
        // Initial check after 3 seconds
        const initialTimer = setTimeout(() => {
            checkForUpdate();
        }, 3000);

        // Periodic check every 30 seconds
        const intervalId = setInterval(() => {
            checkForUpdate();
        }, 30000);

        // Check when user returns to the tab/window
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkForUpdate();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearTimeout(initialTimer);
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkForUpdate]);

    const handleUpdateNow = async () => {
        setIsUpdating(true);

        try {
            // 1. Clear Browser Cache Storage API
            if ('caches' in window) {
                const cacheKeys = await caches.keys();
                await Promise.all(cacheKeys.map(key => caches.delete(key)));
            }

            // 2. Unregister Service Workers if active
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (let registration of registrations) {
                    await registration.unregister();
                }
            }

            // 3. Store reload marker
            sessionStorage.setItem('app_updated_at', Date.now().toString());

        } catch (e) {
            console.error('Error clearing cache before reload:', e);
        } finally {
            // 4. Force hard reload with timestamp parameter to bypass browser file caches
            setTimeout(() => {
                const url = new URL(window.location.href);
                url.searchParams.set('_v', String(latestVersion?.buildTime || Date.now()));
                window.location.href = url.toString();
            }, 400);
        }
    };

    if (!hasUpdate || isDismissed) {
        return null;
    }

    return (
        <div className="fixed bottom-5 right-5 z-[9999] max-w-sm sm:max-w-md w-[calc(100%-2.5rem)] animate-bounce-in">
            <div className="relative overflow-hidden rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-emerald-500/30 p-4 sm:p-5 text-slate-100 shadow-2xl shadow-emerald-950/40 ring-1 ring-white/10">
                {/* Decorative background glow */}
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
                <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl pointer-events-none" />

                <div className="flex items-start gap-3 sm:gap-4 relative z-10">
                    {/* Glowing Icon Badge */}
                    <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-500/25">
                            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 animate-pulse" />
                        </div>
                        {/* Ping indicator */}
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                        </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-6">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-base font-semibold text-white leading-tight">
                                New Update Ready!
                            </h4>
                            {latestVersion?.version && (
                                <span className="text-[11px] font-mono font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    {latestVersion.version}
                                </span>
                            )}
                        </div>
                        <p className="text-xs sm:text-sm text-slate-300 mt-1 leading-relaxed">
                            A new build has been published. Click below to update & automatically clear browser cache.
                        </p>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mt-3 sm:mt-4">
                            <button
                                onClick={handleUpdateNow}
                                disabled={isUpdating}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 active:scale-[0.98] text-white text-xs sm:text-sm font-semibold shadow-lg shadow-emerald-500/25 transition-all duration-150 disabled:opacity-75 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {isUpdating ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <ArrowUpCircle className="w-4 h-4" />
                                        <span>Update & Refresh</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => setIsDismissed(true)}
                                disabled={isUpdating}
                                className="px-3 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors cursor-pointer border border-slate-700/50"
                            >
                                Later
                            </button>
                        </div>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={() => setIsDismissed(true)}
                        disabled={isUpdating}
                        className="absolute top-0 right-0 p-1 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800/60 cursor-pointer"
                        title="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateBanner;
